// Administration: the routes and the sidebar rows of `wealth.platform.administration` (ADR-0027).
//
// The Admin shell (`/admin/*`) is the home of firm administration. It opens for
// a user who holds the granted `admin` token, and it keeps the active persona
// of the session (ADR-0004). The routes under `/view/administration` are the
// addresses before the Admin shell: they redirect to the matching Admin page.
import { defineWebFeature } from '@/features/kit';
import { AdministrationRedirect, IngestPlanRedirect } from '@/pages/admin/AdministrationRedirect';

export default defineWebFeature({
    id: 'wealth.platform.administration',
    routes: [
        {
            // `/admin` opens Home. The Home row has its own address, because a
            // row at `/admin` would own every Admin page as a prefix.
            frontend: 'admin',
            path: '',
            element: <AdministrationRedirect to="/admin/home" />,
        },
        { frontend: 'admin', path: 'home', lazy: () => import('@/pages/admin/page') },
        { frontend: 'admin', path: 'users', lazy: () => import('@/pages/admin/users/page') },
        {
            frontend: 'admin',
            path: 'mcp-connections',
            lazy: () => import('@/pages/admin/mcp-connections/page'),
        },
        { frontend: 'admin', path: 'uploads', lazy: () => import('@/pages/admin/uploads/page') },
        {
            // The review of a prepared upload (SEP-252). It has no sidebar row:
            // the Uploads page and the Admin home lead to it.
            frontend: 'admin',
            path: 'uploads/plans/:id',
            lazy: () => import('@/pages/admin/uploads/plan/page'),
        },
        { frontend: 'admin', path: 'feeds', lazy: () => import('@/pages/admin/feeds/page') },
        {
            frontend: 'admin',
            path: 'book-health',
            lazy: () => import('@/pages/admin/book-health/page'),
        },
        {
            frontend: 'admin',
            path: 'client-books',
            lazy: () => import('@/pages/admin/client-books/page'),
        },
        {
            frontend: 'admin',
            path: 'deleted-contacts',
            lazy: () => import('@/pages/admin/deleted-contacts/page'),
        },
        {
            // SEP-346 and SEP-253: the welcome pack, the firm library and the
            // version of each item of the client literature.
            frontend: 'admin',
            path: 'client-literature',
            lazy: () => import('@/pages/admin/client-literature/page'),
        },
        {
            frontend: 'admin',
            path: 'relationship-roles',
            lazy: () => import('@/pages/admin/relationship-roles/page'),
        },
        {
            frontend: 'admin',
            path: 'tolerances',
            lazy: () => import('@/pages/admin/tolerances/page'),
        },
        { frontend: 'admin', path: 'access', lazy: () => import('@/pages/admin/access/page') },
        {
            frontend: 'admin',
            path: 'features',
            lazy: () => import('@/pages/admin/features/page'),
        },
        {
            frontend: 'view',
            path: 'administration',
            element: <AdministrationRedirect to="/admin/home" />,
        },
        {
            frontend: 'view',
            path: 'administration/welcome-pack',
            element: <AdministrationRedirect to="/admin/client-literature" />,
        },
        {
            frontend: 'view',
            path: 'administration/ingest-plans/:id',
            element: <IngestPlanRedirect />,
        },
    ],
    nav: [
        {
            frontend: 'admin',
            key: 'home',
            label: 'Home',
            href: '/admin/home',
            icon: 'LayoutDashboard',
            group: 'overview',
            order: 100,
        },
        {
            frontend: 'admin',
            key: 'users',
            label: 'Users',
            href: '/admin/users',
            icon: 'Users',
            group: 'people-access',
            order: 200,
        },
        {
            frontend: 'admin',
            key: 'mcp-connections',
            label: 'MCP Connections',
            href: '/admin/mcp-connections',
            icon: 'Plug',
            group: 'people-access',
            order: 210,
        },
        {
            frontend: 'admin',
            key: 'uploads',
            label: 'Uploads',
            href: '/admin/uploads',
            icon: 'Upload',
            group: 'data-in',
            order: 300,
        },
        {
            frontend: 'admin',
            key: 'feeds',
            label: 'Automatic Feeds',
            href: '/admin/feeds',
            icon: 'RefreshCw',
            group: 'data-in',
            order: 310,
        },
        {
            frontend: 'admin',
            key: 'book-health',
            label: 'Book Health',
            href: '/admin/book-health',
            icon: 'HeartPulse',
            group: 'client-book',
            order: 400,
        },
        {
            frontend: 'admin',
            key: 'client-books',
            label: 'Client Books',
            href: '/admin/client-books',
            icon: 'BookUser',
            group: 'client-book',
            order: 410,
        },
        {
            frontend: 'admin',
            key: 'deleted-contacts',
            label: 'Deleted Contacts',
            href: '/admin/deleted-contacts',
            icon: 'UserX',
            group: 'client-book',
            order: 420,
        },
        {
            frontend: 'admin',
            key: 'client-literature',
            label: 'Client Literature',
            href: '/admin/client-literature',
            icon: 'Library',
            group: 'firm-configuration',
            order: 500,
        },
        {
            frontend: 'admin',
            key: 'relationship-roles',
            label: 'Relationship Roles',
            href: '/admin/relationship-roles',
            icon: 'Network',
            group: 'firm-configuration',
            order: 510,
        },
        {
            frontend: 'admin',
            key: 'tolerances',
            label: 'Tolerances',
            href: '/admin/tolerances',
            icon: 'Sliders',
            group: 'firm-configuration',
            order: 520,
        },
        {
            frontend: 'admin',
            key: 'access',
            label: 'Access',
            href: '/admin/access',
            icon: 'KeyRound',
            group: 'audit-access',
            order: 610,
        },
        {
            frontend: 'admin',
            key: 'features',
            label: 'Features',
            href: '/admin/features',
            icon: 'Package',
            group: 'audit-access',
            order: 620,
        },
    ],
});
